import { useCallback } from 'react';

/**
 * Mantiene la posizione di scroll di un elemento tra un mount e l'altro,
 * salvandola in sessionStorage sotto `chiave`.
 *
 * Serve alla sidebar: ogni pagina monta il proprio <Layout>, quindi a ogni
 * navigazione l'<aside> viene ricreato e ripartirebbe da scrollTop 0.
 *
 * È un callback ref (React 19: può restituire una funzione di cleanup), non un
 * useEffect: Layout al primo caricamento rende uno spinner senza sidebar finché
 * i permessi non arrivano, e un effetto partito con ref nullo non riparte più.
 * Il callback invece scatta esattamente quando l'elemento entra nel DOM.
 *
 * Uso: `const ref = useScrollPersistente<HTMLElement>('bop:menu:scroll')`
 * e poi `<aside ref={ref}>`.
 */
export function useScrollPersistente<T extends HTMLElement>(chiave: string) {
  return useCallback((el: T | null) => {
    if (!el) return;

    const salva = () => {
      try { sessionStorage.setItem(chiave, String(Math.round(el.scrollTop))); } catch {}
    };

    // Il ref viene agganciato in fase di commit, prima del paint: niente "salto" visibile.
    try {
      const salvato = sessionStorage.getItem(chiave);
      if (salvato !== null) {
        const top = parseInt(salvato, 10);
        if (!Number.isNaN(top)) el.scrollTop = top;
      }
    } catch {}

    // Scrivere in sessionStorage costa niente: nessun throttling (rAF non gira
    // nelle tab nascoste e perderebbe l'ultima posizione).
    el.addEventListener('scroll', salva, { passive: true });

    return () => {
      el.removeEventListener('scroll', salva);
      // Allo sgancio salviamo comunque, a copertura di scroll programmatici.
      salva();
    };
  }, [chiave]);
}
